import { defineStore } from 'pinia';
import api from '@/api';
import { useLoaderStore } from './loader';
import { useAuthStore } from './auth';

export interface BallsetRule {
  id: number;
  name: string;
  balls_min: number;
  balls_max: number;
  picks_min: number;
  picks_max: number;
  has_bonus: number | boolean;
  max_resulted: number;
  type: string;
}

export interface MarketOutcome {
  id: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

export interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

export interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: BallsetRule[];
}

export interface BetSettings {
  account_id?: number;
  currency_id?: number;
  currency_code?: string;
  currency_symbol: string;
  bet_min: number;
  bet_max: number;
  bet_default: number;
  max_draws: number;
  system_bet_limit: number;
  payload_max?: number;
  bet_options?: {
    stakes: number[];
  };
  bet_amount_values?: number[];
  tax_winnings?: number;
  bet_limit?: number;
  bet_tax?: number;
  tax_deduct_stake?: boolean;
  currency_format?: string;
  allow_decimals: boolean;
  allow_cancel_after_cutoff?: boolean;
  check_duplicates?: boolean;
  check_duplicates_per_customer?: boolean;
  log_failures?: boolean;
  theme?: string;
  show_screen_keyboard?: boolean;
  /** From backend (e.g. book_a_bet) */
  enabled_modules?: string[];
  betslip_inputs?: any;
}

export interface GameDetail {
  id: number;
  name: string;
  slug: string;
  logo: string;
  game_type: {
    name: string;
    slug: string;
  };
  current_draw: {
    event_id: number;
    draw_date: string;
    cutoff: string;
  };
  ballsets: Record<string, any>;
}

export interface GameStats {
  hot_numbers: number[];
  cold_numbers: number[];
  frequency: Record<string, number>;
  missing_frequency: Record<string, number>;
}

export interface RelatedGame {
  slug: string;
  name: string;
  id: number;
  logo?: string | null;
}

export interface GameDetailsResponse {
  game: GameDetail;
  market_groups: MarketGroup[];
  stats: GameStats | null;
  next_draws?: Array<{
    draw_date: string;
    cutoff: string;
    event_id: number;
  }>;
  related_games?: RelatedGame[];
}

export interface Product {
  id: number;
  name: string;
  slug: string;
  logo: string;
  draw_date: string;
  cutoff: string;
  country: string;
}

export interface Country {
  id: number;
  slug: string;
  name: string;
}

export interface ProductsResponse {
  products: Product[];
  countries: Country[];
  server_date: string;
  cached: string;
  show_screen_keyboard: boolean;
  urls: string;
  theme: string;
}

export const useGameStore = defineStore('game', {
  state: () => ({
    gameDetails: null as GameDetailsResponse | null,
    gameSettings: null as BetSettings | null,
    products: null as ProductsResponse | null,
    loading: false,
    error: null as string | null,
    gameSettingsAccount: null as string | null,
    gameSettingsLoading: false,
  }),

  getters: {
    getGameDetail: (state) => state.gameDetails?.game,
    getBetSettings: (state) => state.gameSettings,
    getMarketGroups: (state) => state.gameDetails?.market_groups || [],
    getNextDraws: (state) => state.gameDetails?.next_draws || [],
    getValidDraws: (state) => {
      const draws = state.gameDetails?.next_draws || [];
      const now = new Date();
      return draws.filter((draw: any) => {
        const cutoffTime = new Date(draw.cutoff || draw.draw_date);
        return cutoffTime.getTime() > now.getTime();
      });
    },
    getProducts: (state) => state.products || [],
    getCountries: (state) => state.products?.countries || [],
    getHotNumbers: (state) => state.gameDetails?.stats?.hot_numbers || [],
    getColdNumbers: (state) => state.gameDetails?.stats?.cold_numbers || [],
  },

  actions: {
    async fetchProducts() {
      const loaderStore = useLoaderStore();
      const authStore = useAuthStore();
      this.loading = true;
      this.error = null;
      
      try {
        loaderStore.show('Loading products...');
        const { data } = await api.post('/products', { 
          account: authStore.account
        });
        
        if (data && data.status === 'success' && data.result) {
          this.products = data.result;
        } else {
          throw new Error('Failed to fetch products');
        }
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch products';
        throw e;
      } finally {
        this.loading = false;
        loaderStore.hide();
      }
    },

    async fetchGameDetails(slug: string, showLoader = true) {
      const loaderStore = useLoaderStore();
      const authStore = useAuthStore();
      this.loading = true;
      this.error = null;

      // Get account from auth store
      const account = authStore.account || sessionStorage.getItem('auth_account');
      
      try {
        if (showLoader) {
          loaderStore.show('Loading game details...');
        }
        const { data } = await api.post('/game-details', { slug, account: account });
        
        if (data && data.status === 'success' && data.result) {
          this.gameDetails = data.result;
        } else {
          throw new Error('Failed to fetch game details');
        }
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch game details';
        throw e;
      } finally {
        this.loading = false;
        if (showLoader) {
          loaderStore.hide();
        }
      }
    },

    async refreshDraws() {
      if (!this.gameDetails?.game?.slug) return;

      const authStore = useAuthStore();
      const accountSlug = authStore.account || sessionStorage.getItem('auth_account');

      try {
        const { data } = await api.post('/game-details', { slug: this.gameDetails.game.slug, account: accountSlug });
        
        if (data && data.status === 'success' && data.result) {
          this.gameDetails = data.result;
        }
      } catch (e: any) {
        console.error('Failed to refresh draws:', e);
      }
    },

    clearGameDetails() {
      this.gameDetails = null;
      this.error = null;
    },

    clearProducts() {
      this.products = null;
      this.error = null;
    },

    async fetchGameSettings(showLoader = true, force = false) {
      const loaderStore = useLoaderStore();
      const authStore = useAuthStore();
      
      // Get account from auth store
      const account = authStore.account || sessionStorage.getItem('auth_account');
      
      if (this.gameSettingsLoading) {
        // Wait for current request to complete
        while (this.gameSettingsLoading) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        // Check if the loaded settings match current account
        if (this.gameSettings && this.gameSettingsAccount === account && !force) {
          return;
        }
      }
      
      // Check if we already have settings for this account
      if (!force && this.gameSettings && this.gameSettingsAccount === account) {
        return; // Already loaded, no need to fetch again
      }
      
      if (!account) {
        return;
      }
      
      this.gameSettingsLoading = true;
      this.loading = true;
      this.error = null;
      
      try {
        if (showLoader) {
          loaderStore.show('Loading game settings...');
        }
        
        const { data } = await api.post('/game-settings', {
          account: account
        });
        
        if (data && data.status === 'success' && data.result) {
          this.gameSettings = data.result;
          this.gameSettingsAccount = account; // Track which account these settings belong to
        } else {
          throw new Error('Failed to fetch game settings');
        }
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch game settings';
        throw e;
      } finally {
        this.loading = false;
        this.gameSettingsLoading = false;
        if (showLoader) {
          loaderStore.hide();
        }
      }
    },

    clearGameSettings() {
      this.gameSettings = null;
      this.gameSettingsAccount = null;
      this.error = null;
    },
  },
}); 